import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { formatCurrency } from "../../sdk/utils/display";
import { SliderConfirmBar, NumberPad } from "./fragments";

/** Supported asset IDs for bank transfer (FiClient.bankTransfer) */
export type WalletTransferAssetId = "MORM" | "USDC" | "BTC";

/** Parameters passed to onConfirm for wallet-to-wallet transfer */
export interface WalletTransferConfirmParams {
  to: string;
  assetId: WalletTransferAssetId;
  amount: number;
  memo?: string;
}

export interface WalletTransferModalProps {
  isOpen: boolean;
  toAddress: string;
  assetId: WalletTransferAssetId;
  amount: number;
  availableBalance: number;
  onToAddressChange: (value: string) => void;
  onAssetChange: (value: WalletTransferAssetId) => void;
  onAmountChange: (amount: number) => void;
  onConfirm: (params: WalletTransferConfirmParams) => void;
  onCancel: () => void;
  isTransferring?: boolean;
  onOpen?: () => void;
  memo?: string;
  onMemoChange?: (value: string) => void;
  /** Called when the modal overlay (backdrop) is clicked. Default: no-op. */
  onOverlayClick?: () => void;
}

const ASSET_OPTIONS: { value: WalletTransferAssetId; label: string }[] = [
  { value: "MORM", label: "MORM" },
  { value: "USDC", label: "USDC" },
  { value: "BTC", label: "BTC" },
];

const MIN_AMOUNT = 0.01;

/** Validates an address (basic hex format check for EVM-style addresses) */
function isValidAddress(addr: string): boolean {
  const trimmed = addr?.trim() ?? "";
  return trimmed.length >= 38 && /^0x[a-fA-F0-9]{40}$/.test(trimmed);
}

export function WalletTransferModal({
  isOpen,
  toAddress,
  assetId,
  amount,
  availableBalance,
  onToAddressChange,
  onAssetChange,
  onAmountChange,
  onConfirm,
  onCancel,
  isTransferring = false,
  onOpen,
  memo = "",
  onMemoChange,
  onOverlayClick = () => {},
}: WalletTransferModalProps): JSX.Element | null {
  const [isNumberPadOpen, setIsNumberPadOpen] = useState(false);
  const [isSliderConfirmed, setIsSliderConfirmed] = useState(false);
  const prevIsOpenRef = useRef<boolean>(false);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current && onOpen) {
      onOpen();
    }
    prevIsOpenRef.current = isOpen;
    if (!isOpen) {
      setIsSliderConfirmed(false);
    }
  }, [isOpen, onOpen]);

  const maxAmount = Math.max(MIN_AMOUNT, availableBalance);

  useEffect(() => {
    if (amount > maxAmount && maxAmount >= MIN_AMOUNT) {
      onAmountChange(maxAmount);
    } else if (amount < MIN_AMOUNT && maxAmount >= MIN_AMOUNT) {
      onAmountChange(MIN_AMOUNT);
    }
  }, [maxAmount, amount, onAmountChange]);

  if (!isOpen) return null;

  const isToAddressValid = isValidAddress(toAddress);
  const isAmountValid =
    amount >= MIN_AMOUNT && amount <= maxAmount && availableBalance >= MIN_AMOUNT;
  const isFormValid = isToAddressValid && isAmountValid && assetId;

  const modalContent = (
    <div className="modal-overlay" onClick={onOverlayClick}>
      <div
        className="modal-content transfer-modal-content wallet-transfer-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Transfer to Address</h3>
        </div>

        <div className="modal-body">
          <div className="transfer-field-group">
            <label className="transfer-field-label">To Address</label>
            <input
              type="text"
              value={toAddress}
              onChange={(e) => onToAddressChange(e.target.value)}
              placeholder="0x..."
              className="transfer-select transfer-input"
              disabled={isTransferring}
            />
            {toAddress.trim() && !isToAddressValid && (
              <div className="transfer-field-hint transfer-field-error">
                Enter a valid 0x address (42 characters)
              </div>
            )}
          </div>

          <div className="transfer-field-group">
            <label className="transfer-field-label">Asset</label>
            <select
              value={assetId}
              onChange={(e) => onAssetChange(e.target.value as WalletTransferAssetId)}
              className="transfer-select"
              disabled={isTransferring}
            >
              <option value="">Select asset</option>
              {ASSET_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {assetId && (
            <div className="transfer-field-group">
              <div className="transfer-amount-header">
                <label className="transfer-field-label">Amount</label>
                <div className="transfer-available-balance">
                  Available: {formatCurrency(availableBalance)}
                </div>
              </div>
              {maxAmount >= MIN_AMOUNT ? (
                <>
                  <div
                    className="transfer-amount-display"
                    onClick={() => setIsNumberPadOpen(true)}
                    style={{ cursor: "pointer" }}
                  >
                    <span className="transfer-amount-value">
                      {formatCurrency(amount)}
                    </span>
                  </div>
                  <div className="transfer-amount-container">
                    <input
                      type="range"
                      min={MIN_AMOUNT}
                      max={maxAmount}
                      step={0.01}
                      value={amount}
                      onChange={(e) =>
                        onAmountChange(parseFloat(e.target.value))
                      }
                      className="transfer-amount-slider"
                      style={
                        {
                          "--slider-progress": `${((amount - MIN_AMOUNT) / (maxAmount - MIN_AMOUNT)) * 100}%`,
                        } as React.CSSProperties
                      }
                    />
                    <div className="transfer-amount-labels">
                      <span>{formatCurrency(MIN_AMOUNT)}</span>
                      <span>{formatCurrency(maxAmount)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="transfer-insufficient-funds">
                  Insufficient funds. Minimum required:{" "}
                  {formatCurrency(MIN_AMOUNT)}
                </div>
              )}
            </div>
          )}

          {onMemoChange && (
            <div className="transfer-field-group">
              <label className="transfer-field-label">Memo (optional)</label>
              <input
                type="text"
                value={memo}
                onChange={(e) => onMemoChange(e.target.value)}
                placeholder="Optional memo"
                className="transfer-select transfer-input"
                disabled={isTransferring}
              />
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isTransferring}
          >
            Cancel
          </button>
          <div
            style={{
              flex: 1,
              position: "relative",
              minHeight: "48px",
            }}
          >
            <div
              className="slider-confirm-container"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                opacity: isSliderConfirmed ? 0 : 1,
                visibility: isSliderConfirmed ? "hidden" : "visible",
                transition: "opacity 0.3s ease, visibility 0.3s ease",
                pointerEvents: isSliderConfirmed ? "none" : "auto",
              }}
            >
              <SliderConfirmBar
                onConfirm={() => setIsSliderConfirmed(true)}
                label="Slide to confirm"
                disabled={isTransferring || !isFormValid}
                icon={<img src="/bconfirm.svg" alt="" />}
              />
            </div>
            <button
              onClick={() => {
                onConfirm({
                  to: toAddress.trim(),
                  assetId,
                  amount,
                  ...(memo.trim() && { memo: memo.trim() }),
                });
              }}
              className="btn btn-primary"
              disabled={isTransferring || !isFormValid}
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                opacity: isSliderConfirmed ? 1 : 0,
                visibility: isSliderConfirmed ? "visible" : "hidden",
                transition: "opacity 0.3s ease, visibility 0.3s ease",
                pointerEvents: isSliderConfirmed ? "auto" : "none",
                width: "100%",
                height: "100%",
                color: "black",
              }}
            >
              {isTransferring ? "Processing..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {typeof document !== "undefined"
        ? createPortal(modalContent, document.body)
        : null}
      <NumberPad
        isOpen={isNumberPadOpen}
        value={amount}
        min={MIN_AMOUNT}
        max={maxAmount}
        onConfirm={(value) => {
          onAmountChange(value);
          setIsNumberPadOpen(false);
        }}
        onCancel={() => setIsNumberPadOpen(false)}
      />
    </>
  );
}
